import { useState, useEffect } from 'react';
import { 
  Receipt, Search, ChevronLeft, ChevronRight, 
  ArrowUpCircle, ArrowDownCircle, DollarSign, XCircle, Filter,
  Download, Calendar
} from 'lucide-react';
import api, { getErrorMessage } from '../utils/api';
import YearMonthPicker from '../components/YearMonthPicker';

const TYPE_CONFIG = {
  init:     { label: '初始設定', color: 'bg-blue-100 text-blue-800', icon: DollarSign },
  increase: { label: '預算增加', color: 'bg-green-100 text-green-800', icon: ArrowUpCircle },
  decrease: { label: '預算減少', color: 'bg-orange-100 text-orange-800', icon: ArrowDownCircle },
  expense:  { label: '費用扣款', color: 'bg-red-100 text-red-800', icon: ArrowDownCircle },
};

export default function BudgetTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // 篩選
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);
  
  // 分頁
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 15;

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    setPage(1); // 篩選改變時回到第1頁
  }, [selectedCustomer, selectedType, searchText, dateFrom, dateTo]);

  useEffect(() => {
    fetchTransactions();
  }, [page, selectedCustomer, selectedType, searchText, dateFrom, dateTo]);

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers?status=active');
      const data = res.data.data || res.data;
      setCustomers(data);
    } catch (err) {
      console.error('取得客戶列表失敗:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', perPage);
      if (selectedCustomer) params.append('customer_id', selectedCustomer);
      if (selectedType) params.append('type', selectedType);
      if (searchText.trim()) params.append('search', searchText.trim());
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const res = await api.get(`/budget/transactions/all?${params.toString()}`);
      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      setError(getErrorMessage(err, '載入交易記錄失敗'));
      console.error('取得交易記錄失敗:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = Number(amount);
    const prefix = num > 0 ? '+' : '';
    const hasDecimals = num % 1 !== 0;
    return `${prefix}${new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(num)}`;
  };

  const formatBalance = (value) => {
    const num = Number(value);
    const hasDecimals = num % 1 !== 0;
    return `$${num.toLocaleString('zh-TW', { minimumFractionDigits: 0, maximumFractionDigits: hasDecimals ? 2 : 0 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('zh-TW', {
        timeZone: 'Asia/Taipei',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch {
      return dateStr;
    }
  };

  const clearFilters = () => {
    setSelectedCustomer('');
    setSelectedType('');
    setSearchText('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (selectedCustomer) params.append('customer_id', selectedCustomer);
      if (selectedType) params.append('type', selectedType);
      if (searchText.trim()) params.append('search', searchText.trim());
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      // 將下拉選單顯示的客戶名稱傳給後端作為檔名
      if (selectedCustomer) {
        const customer = customers.find(c => String(c.id) === String(selectedCustomer));
        if (customer) {
          const label = `${customer.customer_name || customer.name} - ${customer.company || '個人'} - ${customer.name}`;
          params.append('customer_label', label);
        }
      }

      const res = await api.get(`/budget/transactions/export-excel?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'] || '';
      let filename = 'budget-transactions.xlsx';
      // 優先解析 filename*=UTF-8''... 格式（支援中文）
      const utf8Match = disposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
      if (utf8Match) {
        filename = decodeURIComponent(utf8Match[1]);
      } else {
        const plainMatch = disposition.match(/filename="?([^";]+)"?/);
        if (plainMatch) filename = plainMatch[1];
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('匯出 Excel 失敗:', err);
      setError(getErrorMessage(err, '匯出 Excel 失敗'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* 標題 */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-indigo-500 rounded-xl">
                <Receipt className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">📋 預算交易記錄</h1>
                <p className="text-gray-500 mt-1">追蹤所有預算增減記錄、受訂單號和其他單號</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleExportExcel}
                disabled={exporting || total === 0}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                <span>{exporting ? '匯出中...' : '匯出 Excel'}</span>
              </button>
              <div className="text-sm text-gray-500">
                共 <span className="font-bold text-indigo-600">{total}</span> 筆記錄
              </div>
            </div>
          </div>
        </div>

        {/* 篩選區 */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-800">篩選條件</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* 客戶篩選 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">客戶</label>
              <select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              >
                <option value="">全部客戶</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.customer_name || c.name} - {c.company || '個人'} - {c.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* 類型篩選 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">交易類型</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              >
                <option value="">全部類型</option>
                {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
            </div>
            
            {/* 搜尋 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">搜尋（單號/說明/備註）</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  placeholder="搜尋受訂單號、其他單號、備註..."
                />
              </div>
            </div>

            {/* 起始日期 */}
            <div>
              <YearMonthPicker
                label="起始日期"
                value={dateFrom}
                onChange={setDateFrom}
                mode="date"
                showDayPicker
              />
            </div>

            {/* 結束日期 */}
            <div>
              <YearMonthPicker
                label="結束日期"
                value={dateTo}
                onChange={setDateTo}
                mode="date"
                dayStrategy="last"
                showDayPicker
              />
            </div>
            
            {/* 清除按鈕 */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
              >
                清除篩選
              </button>
            </div>
          </div>
        </div>

        {/* 錯誤訊息 */}
        {error && (
          <div className="p-4 bg-red-100 text-red-800 rounded-lg flex items-center space-x-2">
            <XCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {/* 交易記錄列表 */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-600">載入中...</span>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-16">
              <Receipt className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">尚無交易記錄</p>
              <p className="text-sm text-gray-400 mt-1">進行預算設定或調整後會自動產生記錄</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">編號</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">日期時間</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">客戶名稱</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">單位</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">姓名</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">類型</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">交易金額</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">交易前餘額</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">交易後餘額</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">受訂單號</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">其他單號</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">說明</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">備註</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((tx) => {
                    const cfg = TYPE_CONFIG[tx.type] || { label: tx.type, color: 'bg-gray-100 text-gray-600', icon: DollarSign };
                    const Icon = cfg.icon;
                    const amount = Number(tx.amount);
                    
                    return (
                      <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-mono">
                          #{tx.id}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {formatDate(tx.created_at)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {tx.customer_name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {tx.company}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {tx.contact_name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center space-x-1 px-2.5 py-1 text-xs font-medium rounded-full ${cfg.color}`}>
                            <Icon className="w-3.5 h-3.5" />
                            <span>{cfg.label}</span>
                          </span>
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm font-bold text-right ${
                          amount > 0 ? 'text-green-600' : amount < 0 ? 'text-red-600' : 'text-gray-600'
                        }`}>
                          {formatCurrency(amount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                          {formatBalance(tx.balance_before)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                          {formatBalance(tx.balance_after)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {tx.order_number || <span className="text-gray-300">-</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {tx.other_number || <span className="text-gray-300">-</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate" title={tx.description}>
                          {tx.description || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate" title={tx.remark}>
                          {tx.remark || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {/* 分頁控制 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
              <p className="text-sm text-gray-600">
                第 {page} / {totalPages} 頁，共 {total} 筆
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(1)}
                  disabled={page <= 1}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  第一頁
                </button>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                {/* 頁碼按鈕 */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        page === pageNum
                          ? 'bg-indigo-500 text-white'
                          : 'border border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  最後一頁
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
